import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";

import FormControl, {
  CheckboxGroupWithSwitchFieldControl,
} from "components/FormControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";

import { Container, ControlRow, Buttons, ButtonContainer } from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues: any;
  isFormLoading?: boolean;
  shouldShowUpdateConfirmation?: boolean;
};

@observer
class AccessForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  @computed get activeProgramsOptions() {
    return ActiveProgramDictionary.options.map(({ value, label }) => ({
      value: { id: value },
      label,
    }));
  }

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit, shouldShowUpdateConfirmation } = this.props;

    if (shouldShowUpdateConfirmation) {
      this.openUpdateConfirmation();
      return;
    }

    onSubmit(values);
  };

  render() {
    const {
      onClose,
      isFormLoading,
      initialValues,
      onChangeIsDirty,
    } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <Row>
            <Typography variant="h1" color="textPrimary">
              Select Access Programs
            </Typography>
          </Row>

          {!isFormLoading ? (
            <ControlRow>
              <CheckboxGroupWithSwitchFieldControl
                name="activePrograms"
                title="Access"
                options={this.activeProgramsOptions}
                switchLabel="Preview only"
                inline
                multi
                required
              />
            </ControlRow>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      Save
    </LoadingButton>
  );
}

export default AccessForm;
